import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enIN } from "date-fns/locale";

const RequiredStar = () => (
  <Typography component="span" color="error" sx={{ ml: 0.5 }}>
    *
  </Typography>
);

const FieldLabel = ({ label, required }) => (
  <Typography
    component="span"
    sx={{
      display: "block",
      mb: 1,
      fontWeight: "medium",
      fontSize: "0.875rem",
    }}
  >
    {label}
    {required && <RequiredStar />}
  </Typography>
);

const StyledTableHead = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: "bold",
}));

const StopSchedulesTable = ({ stopSchedules, errors, handleStopChange }) => {
  // Convert HH:mm to Date object for TimePicker
  const parseTimeToDate = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Convert Date object to HH:mm
  const formatDateToTime = (date) => {
    if (!date || isNaN(date)) return "";
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // Output 24-hour for backend
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#647490" }}>
            <TableRow>
              <StyledTableHead
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontSize: "18px",
                }}
              >
                <Typography component="span">Route Stops</Typography>
              </StyledTableHead>
              <StyledTableHead
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontSize: "18px",
                }}
              >
                <Typography component="span">
                  Arrival Time <RequiredStar />
                </Typography>
              </StyledTableHead>
              <StyledTableHead
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontSize: "18px",
                }}
              >
                <Typography component="span">
                  Departure Time <RequiredStar />
                </Typography>
              </StyledTableHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {stopSchedules.map((stop, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography component="span">{stop.stopName}</Typography>
                </TableCell>
                <TableCell>
                  <TimePicker
                    value={parseTimeToDate(stop.arrivalTime)}
                    onChange={(newValue) =>
                      handleStopChange(index, {
                        target: {
                          name: "arrivalTime",
                          value: formatDateToTime(newValue),
                        },
                      })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        size: "small",
                        error: !!errors[`stop-${index}-arrivalTime`],
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                          },
                        },
                      },
                    }}
                    ampm={true}
                  />
                  {errors[`stop-${index}-arrivalTime`] && (
                    <FormHelperText error>
                      {errors[`stop-${index}-arrivalTime`]}
                    </FormHelperText>
                  )}
                </TableCell>
                <TableCell>
                  <TimePicker
                    value={parseTimeToDate(stop.departureTime)}
                    onChange={(newValue) =>
                      handleStopChange(index, {
                        target: {
                          name: "departureTime",
                          value: formatDateToTime(newValue),
                        },
                      })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        size: "small",
                        error: !!errors[`stop-${index}-departureTime`],
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                          },
                        },
                      },
                    }}
                    ampm={true}
                  />
                  {errors[`stop-${index}-departureTime`] && (
                    <FormHelperText error>
                      {errors[`stop-${index}-departureTime`]}
                    </FormHelperText>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </LocalizationProvider>
  );
};

export default StopSchedulesTable;